import {
  login as keycloakLogin,
  logout as keycloakLogout,
  setAccessToken,
  clearAccessToken,
  refreshToken as refreshAccessToken,
} from '../../services/keycloakService';
import { jwtLogin, jwtLogout } from '../../services/jwtAuthService';
import { fakeLogin, fakeLogout } from '../../services/fakeAuthService';
import { getEnv } from '../../envConfig';
import { jwtDecode } from 'jwt-decode';
import localStorageService from '../../services/localStorageService';
import UserServices from '../../services/UserServices';

const AUTH_METHOD = getEnv().AUTH_METHOD || 'keycloak';
const IGNORED_KEYCLOAK_ROLES = ['offline_access', 'default-roles-codex', 'uma_authorization'];

const normalizeRole = (role = '') => String(role).trim().replace(/\s+/g, ' ').toLowerCase();

const getSelectableRoles = (roles = []) => {
  const ignoredRoleSet = new Set(IGNORED_KEYCLOAK_ROLES.map(normalizeRole));
  const uniqueRoleMap = new Map();

  roles.forEach((role) => {
    const roleName = String(role || '').trim();
    if (!roleName) return;

    const normalizedRole = normalizeRole(roleName);
    if (ignoredRoleSet.has(normalizedRole)) return;

    if (!uniqueRoleMap.has(normalizedRole)) {
      uniqueRoleMap.set(normalizedRole, roleName);
    }
  });

  return Array.from(uniqueRoleMap.values());
};

const buildAuthUser = (decoded, apiUser) => ({
  keycloak_user_id: decoded.sub,
  name: decoded.name,
  full_name: decoded.name,
  email: decoded.email,
  roles: decoded?.realm_access?.roles || [],
  family_name: decoded.family_name,
  given_name: decoded.given_name,
  user: apiUser,
  id: apiUser?.id,
});

const resolveCurrentRole = (availableRoles = [], preferredRole = '') => {
  if (preferredRole && availableRoles.some((role) => normalizeRole(role) === normalizeRole(preferredRole))) {
    return preferredRole;
  }

  return availableRoles[0] || '';
};

export const login = (username, password, selectedRole = null) => async (dispatch) => {
  try {
    let user, accessToken, refreshToken;
    let availableRoles = [];
    if (AUTH_METHOD === 'jwt') {
      const result = await jwtLogin({ username, password });
      user = result.user;
      accessToken = result.accessToken;
      refreshToken = result.refreshToken;
    } else if (AUTH_METHOD === 'fake') {
      const result = await fakeLogin({ username, password });
      user = result.user;
      accessToken = result.accessToken;
      refreshToken = result.refreshToken;
    } else {
      const response = await keycloakLogin({ username, password });
      accessToken = response.data.access_token;
      refreshToken = null;

      const decoded = jwtDecode(accessToken);

      user = {

        keycloak_user_id: decoded.sub,
        name: decoded.name,
        full_name: decoded.name,
        email: decoded.email,
        roles: decoded?.realm_access?.roles || [],
        family_name: decoded.family_name,
        given_name: decoded.given_name,
      };
    }

    setAccessToken(accessToken);

    const userResponse = await UserServices.getAll({
      keycloak_user_id: user.keycloak_user_id,
      page: 0,
      limit: 1,
    });

    const apiUsers = userResponse?.data?.data || userResponse?.data?.view?.data || [];
    user = { ...user, user: apiUsers[0], id: apiUsers[0]?.id }

    const selectableRoles = getSelectableRoles(user?.roles || []);
    availableRoles = selectableRoles;

    if (selectableRoles.length === 0) {
      clearAccessToken();
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: 'No valid application role assigned to this user.',
      });
      return { success: false, error: 'No valid application role assigned to this user.' };
    }

    if (selectableRoles.length > 1 && !selectedRole) {
      clearAccessToken();
      return {
        success: false,
        requiresRoleSelection: true,
        availableRoles: selectableRoles,
      };
    }

    const resolvedRole = selectedRole || selectableRoles[0];
    const selectedRoleIsValid = selectableRoles.some(
      (role) => normalizeRole(role) === normalizeRole(resolvedRole)
    );

    if (!selectedRoleIsValid) {
      clearAccessToken();
      return {
        success: false,
        requiresRoleSelection: true,
        availableRoles: selectableRoles,
        error: 'Selected role is invalid.',
      };
    }

    user = { ...user, roles: [resolvedRole] };

    await localStorageService.setItem('all_user_roles', availableRoles);
    await localStorageService.setItem('selected_user_role', resolvedRole);

    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { user, accessToken, refreshToken, availableRoles, selectedRole: resolvedRole },
    });
    return { success: true };
  } catch (error) {
    clearAccessToken();
    const errorMessage = error?.data?.errorMessage || error?.errorMessage || 'Login failed';
    dispatch({
      type: 'LOGIN_FAILURE',
      payload: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

export const initializeAuth = () => async (dispatch) => {
  try {
    const accessToken = await refreshAccessToken();
    if (!accessToken) {
      dispatch({ type: 'AUTH_BOOTSTRAP_FAILURE' });
      return { success: false };
    }

    setAccessToken(accessToken);

    const decoded = jwtDecode(accessToken);
    const userResponse = await UserServices.getAll({
      keycloak_user_id: decoded.sub,
      page: 0,
      limit: 1,
    });

    const apiUsers = userResponse?.data?.data || userResponse?.data?.view?.data || [];
    const apiUser = apiUsers[0];
    const user = buildAuthUser(decoded, apiUser);
    const availableRoles = getSelectableRoles(user?.roles || []);
    const preferredRole = localStorageService.getItem('selected_user_role');
    const resolvedRole = resolveCurrentRole(availableRoles, preferredRole);

    if (availableRoles.length === 0) {
      clearAccessToken();
      dispatch({ type: 'AUTH_BOOTSTRAP_FAILURE' });
      return { success: false };
    }

    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: {
        user: { ...user, roles: resolvedRole ? [resolvedRole] : availableRoles.slice(0, 1) },
        accessToken,
        refreshToken: null,
        availableRoles,
        selectedRole: resolvedRole,
      },
    });

    await localStorageService.setItem('all_user_roles', availableRoles);
    if (resolvedRole) {
      await localStorageService.setItem('selected_user_role', resolvedRole);
    }

    return { success: true };
  } catch (error) {
    clearAccessToken();
    dispatch({ type: 'AUTH_BOOTSTRAP_FAILURE' });
    return { success: false };
  }
};

export const logout = () => async (dispatch) => {
  try {
    if (AUTH_METHOD === 'jwt') {
      await jwtLogout();
    } else if (AUTH_METHOD === 'fake') {
      await fakeLogout();
    } else {
      await keycloakLogout();
    }
  } catch (e) { }
  // Remove from localStorage using async service
  clearAccessToken();
  await localStorageService.removeItem('all_user_roles');
  await localStorageService.removeItem('selected_user_role');
  dispatch({ type: 'LOGOUT' });
};
